import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';

const invalidateDesignationQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['designations'] });
  queryClient.invalidateQueries({ queryKey: ['designation-select'] });
};

export const useFetchDesignations = ({ operatorId, page = 1, search = '', limit = 10 } = {}) => {
  return useQuery({
    queryKey: ['designations', operatorId, page, search, limit],
    queryFn: async () => {
      const response = await httpService.post('/designation/list', {
        operatorId,
        offset: page,
        limit,
        search: search || undefined,
      });
      return response.data.data;
    },
    keepPreviousData: true,
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
  });
};

export const useFetchDesignationSelect = ({ operatorId, search = '' } = {}, options = {}) => {
  return useQuery({
    queryKey: ['designation-select', operatorId, search],
    queryFn: async () => {
      const response = await httpService.post('/designation/get-designation-select', {
        operatorId,
        search: search || undefined,
      });
      return response.data.data?.paginatedData || [];
    },
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useCreateDesignation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['create-designation'],
    mutationFn: async (data) => {
      const response = await httpService.post('/designation/create', data);
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateDesignationQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useUpdateDesignation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['update-designation'],
    mutationFn: async ({ id, ...data }) => {
      const response = await httpService.post(`/designation/update/${id}`, data);
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateDesignationQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useDeleteDesignation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['delete-designation'],
    mutationFn: async ({ id, operatorId }) => {
      const response = await httpService.post(`/designation/delete/${id}`, { operatorId });
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateDesignationQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};
